import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { getRecentChatAvatarsAction, RecentChatAvatarWithUrl } from './actions';
import { createCloudinaryImageSync } from '@/app/lib/cloudinary/cloudinary-image-utils';

interface UseRecentChatsOptions {
  initialData?: RecentChatAvatarWithUrl[];
  skipInitialFetch?: boolean;
}

export function useRecentChats(options: UseRecentChatsOptions = {}) {
  const { initialData = [], skipInitialFetch = false } = options;
  const { session, userEmail } = useAuth();
  const [recentChats, setRecentChats] = useState<RecentChatAvatarWithUrl[]>(initialData);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(initialData.length > 0);

  const fetchRecentChats = useCallback(async () => {
    if (!session || !userEmail) {
      setRecentChats([]);
      return;
    }
    
    setIsLoadingChats(true);
    try {
      const { success, avatars } = await getRecentChatAvatarsAction(userEmail, 1000);
      if (success && avatars) {
        // Create Cloudinary images for avatar images on the client side
        const avatarsWithCloudinaryImages = avatars.map((avatar) => {
          if (avatar.image_uri) {
            try {
              const cloudinaryImage = createCloudinaryImageSync(avatar.image_uri);
              return {
                ...avatar,
                cloudinaryImage
              };
            } catch (error) {
              console.error(`Failed to create Cloudinary image for avatar ${avatar.avatar_id}:`, error);
              return avatar;
            }
          }
          return avatar;
        });
        setRecentChats(avatarsWithCloudinaryImages);
      } else {
        setRecentChats([]);
      }
    } catch (error) {
      console.error('Error fetching recent chats:', error);
      setRecentChats([]);
    } finally {
      setIsLoadingChats(false);
      setHasInitialized(true);
    }
  }, [session, userEmail]);

  // Initial fetch only if not skipped and no initial data
  useEffect(() => {
    if (!skipInitialFetch && !hasInitialized) {
      fetchRecentChats();
    }
  }, [fetchRecentChats, skipInitialFetch, hasInitialized]);

  // Refresh function that can be called externally
  const refreshRecentChats = useCallback(() => {
    fetchRecentChats();
  }, [fetchRecentChats]);

  return {
    recentChats,
    isLoadingChats,
    refreshRecentChats
  };
} 